/**
 * Midgard application shell — mounted once as a layout route, so the top
 * bar, navigation and their state persist while pages change underneath.
 * Geometry lives in shell.css; pages never offset themselves for chrome.
 */
import { useState, useRef, useCallback } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useNotifications } from '../../context/NotificationContext';
import NotificationDropdown from '../notifications/NotificationDropdown';
import {
  Search, PlusSquare, Plus, User, Settings, LogOut, Sun, Moon,
  DollarSign, Coins, Megaphone, Bell, Shield, Vault, ArrowLeft, Info,
} from 'lucide-react';
import {
  HearthIcon, WayfinderIcon, SagasIcon, TheRealmsIcon,
  RavenpostIcon, TheHeraldIcon, KeepsakeIcon,
} from '../icons/NorseIcons';
import { RealmsNav } from '../social/socialModules';
import BrandMark from '../brand/BrandMark';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import CreatePostModal from '../post/CreatePostModal';
import SiteFooter from '../site/SiteFooter';
import LegalGate from '../legal/LegalGate';
import DesktopRightRail, { HIDDEN_ROUTES as RIGHT_RAIL_HIDDEN } from './DesktopRightRail';
import odinFace from '../../assets/odin/odin-face.webp';
import { useBack, useKeyboardState, useReleaseCheck, useScrollRestoration } from './shellHooks';
import './shell.css';

export const OdinGlyph = ({ className = '' }) => (
  <img src={odinFace} alt="" aria-hidden="true" className={`mg-odin-glyph ${className}`} draggable={false} />
);

// The six destinations of the phone bar, in the owner's order.
const PRIMARY = [
  { icon: HearthIcon,    label: 'The Hearth', short: 'Hearth',    path: '/home' },
  { icon: WayfinderIcon, label: 'Wayfinder',  short: 'Wayfinder', path: '/explore' },
  { icon: SagasIcon,     label: 'Sagas',      short: 'Sagas',     path: '/reels' },
  { icon: TheRealmsIcon, label: 'The Realms', short: 'Realms',    path: '/world', prefix: true },
  { icon: RavenpostIcon, label: 'Ravenpost',  short: 'Ravenpost', path: '/messages' },
  { icon: OdinGlyph,     label: 'oDIN',       short: 'oDIN',      path: '/odin', feature: true },
];

// Where "back" goes when there is no in-app history (deep link, fresh PWA launch).
function parentOf(pathname) {
  if (PRIMARY.some((p) => pathname === p.path)) return null;      // a primary section: no back
  if (pathname.startsWith('/world')) return null;                    // world pages carry their own back
  if (pathname.startsWith('/wallet/')) return '/wallet';
  return '/home';
}

const isActive = (pathname, item) =>
  item.prefix ? pathname === item.path || pathname.startsWith(item.path + '/') : pathname === item.path;

export default function MainLayout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { unreadCount } = useNotifications();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const bellRef = useRef(null);
  const toggleNotifDropdown = useCallback(() => setShowNotifDropdown((v) => !v), []);

  useKeyboardState();
  useScrollRestoration();
  useReleaseCheck();

  const parent = parentOf(pathname);
  const goBack = useBack(parent || '/home');
  const asideOn = !RIGHT_RAIL_HIDDEN.some((p) => pathname.startsWith(p));
  const isStaff = user?.role === 'moderator' || user?.role === 'admin';
  const badge = unreadCount > 99 ? '99+' : unreadCount;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const railLink = (to, label, Icon, { feature = false, active, count, testid, tone } = {}) => (
    <Link
      key={to}
      to={to}
      className={`mg-rail-link${feature ? ' is-feature' : ''}`}
      aria-current={active ? 'page' : undefined}
      title={label}
      data-testid={testid}
      style={tone && !active ? { color: tone } : undefined}
    >
      <Icon className="w-5 h-5" />
      <span className="mg-rail-label">{label}</span>
      {count ? <span className="mg-rail-count" data-testid="sidebar-notification-badge">{count}</span> : null}
    </Link>
  );

  return (
    <div className="mg-shell" data-aside={asideOn ? 'on' : 'off'}>
      {/* ── Top bar ── */}
      <header className="mg-topbar" data-testid="shell-topbar">
        <div className="mg-topbar-brand">
          {parent && (
            <button type="button" onClick={goBack} className="mg-topbar-icon inline-flex md:hidden" aria-label="Back" data-testid="shell-back">
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <Link to="/home" className="flex items-center gap-2.5 min-w-0" data-testid="brand-logo-link" aria-label="Midgard home">
            <BrandMark size={32} />
            <span
              className={`mg-wordmark text-base text-foreground ${parent ? 'hidden' : 'hidden min-[380px]:inline'} md:hidden xl:inline`}
              style={{ letterSpacing: '0.12em' }}
            >
              Midgard
            </span>
          </Link>
        </div>

        <div className="mg-topbar-center">
          {parent && (
            <button type="button" onClick={goBack} className="mg-topbar-icon hidden md:inline-flex" aria-label="Back" data-testid="shell-back-wide">
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <Link
            to="/search"
            className="hidden lg:flex flex-1 max-w-md mx-auto items-center gap-3 px-4 h-10 bg-[hsl(var(--mg-surface)/0.5)] border border-[hsl(var(--mg-steel)/0.2)] rounded-lg text-muted-foreground hover:border-[hsl(var(--mg-steel)/0.35)] transition-colors"
          >
            <Search className="w-4 h-4" />
            <span className="text-sm">Search Midgard...</span>
          </Link>
        </div>

        <div className="mg-topbar-actions">
          <Link to="/search" className="mg-topbar-icon inline-flex lg:hidden" data-testid="mobile-search-btn" aria-label="Search">
            <Search className="w-5 h-5" />
          </Link>

          <Link
            to="/wallet"
            className="hidden min-[520px]:flex items-center gap-1.5 h-9 px-2.5 rounded-lg bg-[hsl(var(--mg-gold)/0.08)] border border-[hsl(var(--mg-gold-deep)/0.25)] hover:bg-[hsl(var(--mg-gold)/0.14)] transition-colors"
            data-testid="coins-display"
            title="Runegold Wallet"
          >
            <Coins className="w-4 h-4 text-[hsl(var(--mg-gold))]" />
            <span className="text-sm font-semibold text-foreground tabular-nums">{user?.wonder_coins || 0}</span>
          </Link>

          <button
            type="button"
            onClick={() => setShowCreatePost(true)}
            className="mg-topbar-icon inline-flex lg:hidden"
            aria-label="Create post"
            data-testid="nav-create"
          >
            <Plus className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => setShowCreatePost(true)}
            className="mg-btn mg-btn-primary hidden lg:flex items-center gap-2 h-9 px-4 text-sm"
            data-testid="create-post-btn"
          >
            <PlusSquare className="w-4 h-4" />
            <span>Create</span>
          </button>

          <button
            type="button"
            onClick={toggleTheme}
            className="mg-topbar-icon hidden md:inline-flex"
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            data-testid="theme-toggle-btn"
          >
            {theme === 'dark' ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
          </button>

          <div className="relative">
            <button
              type="button"
              ref={bellRef}
              onClick={toggleNotifDropdown}
              className="mg-topbar-icon inline-flex"
              data-testid="header-notification-bell"
              aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && <span className="mg-badge" data-testid="notification-badge">{badge}</span>}
            </button>
            <NotificationDropdown open={showNotifDropdown} onClose={() => setShowNotifDropdown(false)} triggerRef={bellRef} />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="mg-topbar-icon inline-flex" data-testid="user-menu-btn" aria-label="Account menu">
                <Avatar className="w-8 h-8 border border-[hsl(var(--mg-steel)/0.3)]">
                  <AvatarImage src={user?.avatar} alt="" />
                  <AvatarFallback className="text-xs">{user?.display_name?.[0]}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              collisionPadding={8}
              className="w-60 max-h-[calc(var(--app-h)-var(--shell-top)-16px)] overflow-y-auto bg-card border-[hsl(var(--mg-steel)/0.25)] rounded-lg"
            >
              <DropdownMenuItem onClick={() => navigate(`/profile/${user?.username}`)} className="min-h-10" data-testid="profile-menu-item">
                <User className="w-4 h-4 mr-2" /> Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/odin')} className="min-h-12 items-center" data-testid="odin-menu-item">
                <OdinGlyph className="w-5 h-5 mr-2" />
                <span className="flex flex-col leading-tight">
                  <span>oDIN</span>
                  <span className="text-[11px] text-muted-foreground">Speak with the Oracle</span>
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/notifications')} className="min-h-10 md:hidden" data-testid="herald-menu-item">
                <TheHeraldIcon className="w-4 h-4 mr-2" /> The Herald
                {unreadCount > 0 && <span className="ml-auto text-[11px] font-semibold text-[hsl(var(--mg-purple))]">{badge}</span>}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/bookmarks')} className="min-h-10" data-testid="bookmarks-menu-item">
                <KeepsakeIcon className="w-4 h-4 mr-2" /> Keepsakes
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/creator-dashboard')} className="min-h-10" data-testid="earnings-menu-item">
                <DollarSign className="w-4 h-4 mr-2" /> Creator Dashboard
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/wallet')} className="min-h-10" data-testid="wallet-menu-item">
                <Coins className="w-4 h-4 mr-2 text-[hsl(var(--mg-gold))]" /> Runegold Wallet
                <span className="ml-auto text-[11px] tabular-nums text-muted-foreground min-[520px]:hidden">{user?.wonder_coins || 0}</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/ads')} className="min-h-10" data-testid="ads-menu-item">
                <Megaphone className="w-4 h-4 mr-2" /> Advertising
              </DropdownMenuItem>
              {isStaff && (
                <DropdownMenuItem onClick={() => navigate('/moderation')} className="min-h-10 md:hidden">
                  <Shield className="w-4 h-4 mr-2" /> Wardens' Hall
                </DropdownMenuItem>
              )}
              {user?.role === 'admin' && (
                <DropdownMenuItem onClick={() => navigate('/treasury')} className="min-h-10 md:hidden">
                  <Vault className="w-4 h-4 mr-2" /> Treasury
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => navigate('/settings')} className="min-h-10" data-testid="settings-menu-item">
                <Settings className="w-4 h-4 mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/about')} className="min-h-10" data-testid="about-menu-item">
                <Info className="w-4 h-4 mr-2" /> About &amp; legal
              </DropdownMenuItem>
              <DropdownMenuItem onClick={toggleTheme} className="min-h-10 md:hidden" data-testid="theme-menu-item">
                {theme === 'dark' ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                {theme === 'dark' ? 'Light mode' : 'Dark mode'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="min-h-10 text-destructive" data-testid="logout-menu-item">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* ── Left rail: icons at 768+, full sidebar at 1280+ ── */}
      <aside className="mg-rail" aria-label="Main navigation">
        <nav className="flex flex-col gap-1">
          {PRIMARY.slice(0, 5).map((item) =>
            railLink(item.path, item.label, item.icon, {
              feature: item.prefix, active: isActive(pathname, item), testid: `sidebar-${item.path.slice(1)}`,
            }))}
          {railLink('/notifications', 'The Herald', TheHeraldIcon, {
            active: pathname === '/notifications', count: unreadCount > 0 ? badge : null, testid: 'sidebar-notifications',
          })}
          {railLink('/odin', 'oDIN', OdinGlyph, { feature: true, active: pathname === '/odin', testid: 'sidebar-odin' })}
          {railLink('/bookmarks', 'Keepsakes', KeepsakeIcon, { active: pathname === '/bookmarks', testid: 'sidebar-bookmarks' })}
          {isStaff && railLink('/moderation', "Wardens' Hall", Shield, {
            active: pathname === '/moderation', testid: 'sidebar-moderation', tone: 'rgb(217 119 6 / .9)',
          })}
          {user?.role === 'admin' && railLink('/treasury', 'Treasury', Vault, {
            active: pathname === '/treasury', testid: 'sidebar-treasury', tone: 'hsl(var(--mg-gold) / .85)',
          })}
        </nav>

        <div className="mg-rail-full"><RealmsNav /></div>

        <div className="mt-auto pt-3">
          <Link
            to={`/profile/${user?.username}`}
            className="mg-rail-link"
            aria-current={pathname === `/profile/${user?.username}` ? 'page' : undefined}
            title="Your profile"
          >
            <Avatar className="w-8 h-8 shrink-0 border border-[hsl(var(--mg-steel)/0.25)]">
              <AvatarImage src={user?.avatar} alt="" />
              <AvatarFallback className="text-xs">{user?.display_name?.[0]}</AvatarFallback>
            </Avatar>
            <span className="mg-rail-label flex flex-col leading-tight">
              <span className="font-semibold text-sm text-foreground truncate">{user?.display_name}</span>
              <span className="text-xs text-muted-foreground truncate">@{user?.username}</span>
            </span>
          </Link>
        </div>
      </aside>

      {/* ── Page ── */}
      <main className="mg-shell-main" id="main">
        <Outlet />
        {/* Desktop footer on scrolling pages; full-viewport pages (stage) have none.
            On phones the same links live on /about, reached from the account menu. */}
        {asideOn && <SiteFooter className="hidden lg:block mt-12" />}
      </main>

      {asideOn && <DesktopRightRail />}

      {/* ── Bottom navigation: phones ── */}
      <nav className="mg-bottomnav" aria-label="Main navigation" data-testid="shell-bottomnav">
        {PRIMARY.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className="mg-bottomnav-link"
            aria-current={isActive(pathname, item) ? 'page' : undefined}
            aria-label={item.label}
            data-testid={`nav-${item.path.slice(1)}`}
          >
            <item.icon className="w-6 h-6" />
            <span className="mg-bottomnav-label">{item.short}</span>
          </Link>
        ))}
      </nav>

      <CreatePostModal open={showCreatePost} onOpenChange={setShowCreatePost} />
      <LegalGate />
    </div>
  );
}
